import { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import { useLocationSearch } from "../../hooks/useMasterdataSearch";
import AsyncAutocompleteWithQuickCreate from "../common/AsyncAutocompleteWithQuickCreate";
import type { Location } from "../../features/masterdata/types";

export interface TransferStopLocationFieldProps {
  /** Stable key for this stop (e.g. stop.id or generated uuid). Isolates search state per stop. */
  stopKey: string;
  /** Selected location id (empty string when none). */
  value: string;
  onChange: (locationId: string | null) => void;
  /** Controlled search input value. */
  inputValue: string;
  onInputChange: (value: string) => void;
  disabled?: boolean;
  error?: string;
  label?: string;
  showCreate?: boolean;
  createLabel?: string;
  onOpenQuickCreate?: () => void;
  /** When user just created a location for this stop, pass it so it appears in options. */
  optionalNewLocation?: Location | null;
}

/**
 * Location search field fully isolated per stop via stopKey.
 * Each instance has its own useLocationSearch (own options, loading, debounced search).
 * Use one instance per stop so Stop A and Stop B can search independently.
 */
export default function TransferStopLocationField({
  stopKey,
  value,
  onChange,
  inputValue,
  onInputChange,
  disabled = false,
  error,
  label = "Location",
  showCreate = true,
  createLabel = "Create new location",
  onOpenQuickCreate,
  optionalNewLocation,
}: TransferStopLocationFieldProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const locationSearch = useLocationSearch(value || null, {
    menuOpen,
    controlledSearchTerm: inputValue,
    onSearchTermChange: onInputChange,
    stopKey,
  });

  const options = useMemo(
    () => [...locationSearch.options, ...(optionalNewLocation ? [optionalNewLocation] : [])],
    [locationSearch.options, optionalNewLocation]
  );

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <AsyncAutocompleteWithQuickCreate<Location>
        label={label}
        value={value}
        onChange={(id) => onChange(id ?? null)}
        options={options}
        getOptionLabel={(l) => l.name}
        showCreate={showCreate}
        createLabel={createLabel}
        onOpenQuickCreate={onOpenQuickCreate}
        loading={locationSearch.loading}
        inputValue={inputValue}
        onInputChange={(_, v) => onInputChange(v)}
        filterOptions={(opts) => opts}
        onOpen={() => setMenuOpen(true)}
        onClose={() => setMenuOpen(false)}
        placeholder="Type to search…"
        disabled={disabled}
        error={error}
      />
    </Box>
  );
}
